import { useCallback, useEffect } from 'react'
import { css } from '@emotion/react'
import { Link, navigate } from 'gatsby'
import { string, bool, array } from 'prop-types'
import { Checkbox, DesignToken } from '@livechat/design-system-react-components'

import { useEnhancedList } from '@/context/EnhancedList'
import { convertObjectToParams } from '@/utils/browser'
import { handleCategorySelect } from '@/utils/products'
import { noop } from '@/utils/func'
import { URLS, FILTERS, APPS_LIST_STORAGE_KEY } from '@/constants'
import Icon from '@/components/Icon'
import Collapse from '@/components/Collapse'

const collapseMenuCss = css`
  .collapse__label {
    align-items: flex-start;
    justify-content: space-between;

    svg {
      max-width: 20px;
      max-height: 20px;
      margin-right: 10px;
    }

    > a {
      display: flex;
      width: 100%;
      font-size: 16px;
      line-height: 16px;
      text-transform: none;
      margin-left: 0;
      margin-bottom: 10px;
    }
  }

  .collapse__content {
    a {
      margin: 0;
    }
  }
`

const categoryFiltersCss = css`
  margin-top: 5px;
  margin-left: 38px;

  .ds-checkbox {
    width: 100%;

    label {
      display: flex;
      text-transform: none;
      font-weight: normal;
      font-size: 16px;
      line-height: 28px;
      margin-bottom: 14px;

      &:hover div {
        color: var(${DesignToken.ActionPrimaryHover});
      }
    }
  }
`

const Categories = ({ label = 'All apps', isOpenOnInit, options, ...rest }) => {
  const { filters, filtersOptions, sort, updateFilters, clearFilter } =
    useEnhancedList()

  const categoriesFilter = filters?.[FILTERS.CATEGORY.ID]
  const categoriesFilterOptions =
    options || filtersOptions?.[FILTERS.CATEGORY.ID] || []

  const handleCategoriesFilterClick = useCallback(
    (category) => {
      handleCategorySelect({
        value: category.VALUE,
        filters,
        sort,
        updateFilters,
      })
    },
    [filters, sort, updateFilters]
  )

  useEffect(
    () => {
      const categories = filters?.categories

      if (Array.isArray(categories) && categories?.length === 1) {
        const preservedQueryParams = convertObjectToParams({
          ...filters,
          sortBy: sort,
        })

        const isAppsPage = String(window.location.pathname).includes('/apps')
        const url = `/apps/${categories[0]}/${preservedQueryParams}`
        const currentURL = window.location.pathname + window.location.search

        if (isAppsPage && currentURL !== url) {
          navigate(url, { replace: true })
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters]
  )

  return (
    <Collapse
      isOpenOnInit={isOpenOnInit}
      sectionKey={FILTERS.CATEGORY.ID}
      sectionLink={URLS.APPS.LINK}
      storageKey={APPS_LIST_STORAGE_KEY}
      css={collapseMenuCss}
      label={({ inputRef, isContentVisible }) => {
        const handleOnClick = () => {
          if (!inputRef.current.checked) {
            inputRef.current.click()
          }

          if (clearFilter) {
            clearFilter(FILTERS.CATEGORY.ID)
          }
        }

        return (
          <Link
            to="/apps/"
            onClick={handleOnClick}
            activeClassName={isContentVisible ? 'active-link' : ''}
          >
            <Icon label={URLS.APPS.LABEL} />
            <span>{label}</span>
          </Link>
        )
      }}
      content={
        <div css={categoryFiltersCss} data-test="category-filters">
          {categoriesFilterOptions.map((category) => {
            const handleOnClick = (e) => {
              e.preventDefault()
              handleCategoriesFilterClick(category)
            }

            const isChecked = categoriesFilter
              ? categoriesFilter.includes(category.VALUE)
              : false

            return (
              <Link
                key={`category-${category.ID}`}
                to={`/apps/${category.VALUE}/`}
                onClick={handleOnClick}
              >
                <Checkbox
                  className="ds-checkbox"
                  id={category.ID}
                  checked={isChecked}
                  onChange={noop}
                  data-test="category-checkbox"
                >
                  {category.LABEL}
                </Checkbox>
              </Link>
            )
          })}
        </div>
      }
      {...rest}
    />
  )
}

Categories.propTypes = {
  label: string,
  isOpenOnInit: bool,
  options: array,
}

export default Categories
